import type { Topic } from '@/@types/topic'
import { FormAttachmentSchema, InPayloadAttachmentsSchema } from '@/components/Attachment/schema'
import {
	filterLinkAttachments,
	filterPicAttachments,
	filterVideoAttachments,
	toAttachments,
} from '@/components/Attachment/utils'
import { LinkInfoSchema } from '@/utils/fetchers/attachment'
import { compact, isEmpty } from 'lodash-es'
import { z } from 'zod'

// 定义表单验证 schema
export const PostFormSchema = z.object({
	// 帖子内容验证
	content: z.string(),

	attachment: FormAttachmentSchema,

	// 话题验证
	topic: z.custom<Topic>().optional(),

	// ID 用于区分创建/编辑模式
	id: z.string().optional(),
})

// 导出类型
export type PostFormValue = z.infer<typeof PostFormSchema>

export const CreatePostPayloadSchema = z
	.object({
		coord: z
			.object({
				coordType: z.string(),
				lat: z.string(),
				lng: z.string(),
			})
			.optional(),
		pictureKeys: z.array(z.string()).optional(),
		videoKey: z.string().optional(),
		linkInfo: z
			.object({
				supplementary: z.boolean(),
				urlsInText: z.array(z.string()),
				linkType: z.literal('default'),
			})
			.merge(LinkInfoSchema)
			.optional(),
		submitToTopic: z.string().optional(),
		syncToPersonalUpdate: z.boolean(),
	})
	.merge(
		PostFormSchema.pick({
			content: true,
			topic: true,
			attachment: true,
		})
	)
	.transform(async ({ content, attachment, topic, syncToPersonalUpdate }, ctx) => {
		const attachments = await InPayloadAttachmentsSchema.parseAsync(toAttachments(attachment))
		const pictureKeys = compact(filterPicAttachments(attachments).map((item) => item.cdn?.file.key))
		const videoKey = filterVideoAttachments(attachments)[0]?.cdn?.file?.key
		const linkInfo = compact(filterLinkAttachments(attachments).map((item) => item.linkInfo))[0]

		if (!isEmpty(videoKey) && isEmpty(content)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '请输入文字后发布',
				path: ['content'],
			})
		}

		return {
			content,
			pictureKeys,
			videoKey,
			linkInfo: linkInfo
				? {
						...linkInfo,
						supplementary: false,
						urlsInText: [],
						linkType: 'default',
					}
				: undefined,
			submitToTopic: topic?.id,
			syncToPersonalUpdate,
		}
	})

export type CreatePostPayload = z.infer<typeof CreatePostPayloadSchema>

const EditTypeSchema = z.enum(['IMAGE', 'VIDEO', 'LINK', 'TEXT'])
type EditTypeType = z.infer<typeof EditTypeSchema>

export const UpdatePostPayloadSchema = z
	.object({
		id: z.string(),
		editType: EditTypeSchema.optional(),
		pictureKeys: z.array(z.string()).optional(),
		videoKey: z.string().optional(),
		linkInfo: z
			.object({
				supplementary: z.boolean(),
				urlsInText: z.array(z.string()),
				linkType: z.literal('default'),
			})
			.merge(LinkInfoSchema)
			.optional(),
		submitToTopic: z.string().optional(),
	})
	.merge(
		PostFormSchema.pick({
			content: true,
			topic: true,
			attachment: true,
		})
	)
	.transform(async ({ content, attachment, topic, id }, ctx) => {
		const attachments = await InPayloadAttachmentsSchema.parseAsync(toAttachments(attachment))
		const pictureKeys = compact(filterPicAttachments(attachments).map((item) => item.cdn?.file.key))
		const video = filterVideoAttachments(attachments)[0]?.cdn?.file
		const videoKey = video?.key
		const linkInfo = compact(filterLinkAttachments(attachments).map((item) => item.linkInfo))[0]
		let editType: EditTypeType | undefined = undefined

		switch (true) {
			case !!video?.fileUrl:
				editType = EditTypeSchema.enum.VIDEO
				break
			case !isEmpty(pictureKeys):
				editType = EditTypeSchema.enum.IMAGE
				break
			case !!linkInfo:
				editType = EditTypeSchema.enum.LINK
				break
			default:
				editType = EditTypeSchema.enum.TEXT
				break
		}

		return {
			id,
			editType,
			content,
			pictureKeys,
			videoKey,
			linkInfo: linkInfo
				? {
						...linkInfo,
						supplementary: false,
						urlsInText: [],
						linkType: 'default',
					}
				: undefined,
			submitToTopic: topic?.id,
		}
	})

export type UpdatePostPayload = z.infer<typeof UpdatePostPayloadSchema>
